import { faAngleDown, faWarning } from '@fortawesome/pro-solid-svg-icons';
import useAccountInfo from 'hooks/account/useAccountInfo';
import useMultiOrgAccessRequestList from 'hooks/useMultiOrgAccessRequestList';
import Image from 'next/image';
import { useState } from 'react';
import { Avatar } from 'stylus-ui/Avatar';
import { Button } from 'stylus-ui/Button';
import { DropdownMenu, DropdownMenuCheckboxItem, DropdownMenuContent, DropdownMenuTrigger } from 'stylus-ui/DropdownMenu';
import { TableCell, TableRow } from 'stylus-ui/Table';
import { toast } from 'stylus-ui/Toast';
import { cn } from 'utils/cn';

import { trackEvent } from '../../../../utils/tracking';
import PermissionLevel from '../InviteReferralModal/PermissionLevel';

export const PERMISSION_LEVELS = {
  admin: 'Admin',
  write: 'Creator'
};
export const ENTERPRISE_PERMISSION_LEVELS = { ...PERMISSION_LEVELS, read: 'Viewer' };

export interface UserRequestProps {
  teamId: string;
  teamName: string;
  email: string;
  name: string;
  isEnterpriseSuperOrg: boolean;
  profilePictureUrl?: string;
}

type RequestProfileCardProps = {
  profilePictureUrl: string;
  name: string;
  email: string;
};

type PermissionLevelDropdownProps = {
  isEnterpriseSuperOrg: boolean;
  permissionLevel: string;
  setPermissionLevel: (permissionLevel: PermissionLevel) => void;
};

type ApproveRequestDropdownProps = {
  teamId: string;
  email: string;
  permissionLevel: PermissionLevel;
};

const RequestProfileCard = ({ profilePictureUrl, name, email }: RequestProfileCardProps) => {
  return (
    <div className='flex items-center'>
      <Avatar size='sm'>
        {profilePictureUrl ? <Image src={profilePictureUrl} width={24} height={24} alt='user-request-access-profile' /> : email}
      </Avatar>
      <div className='ml-2 flex max-w-[170px] flex-col'>
        {name && <span className='truncate text-xs font-medium leading-[13px] text-slate-700'>{name}</span>}
        <span
          className={cn('truncate text-sm font-normal text-slate-700', {
            '!text-xs !leading-[13px] !text-slate-500': !!name
          })}
        >
          {email}
        </span>
      </div>
    </div>
  );
};

const PermissionLevelDropdown = ({ isEnterpriseSuperOrg, permissionLevel, setPermissionLevel }: PermissionLevelDropdownProps) => {
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button icon={faAngleDown} variant='secondary' iconPosition='right' size='small' className='min-w-[84px] justify-between'>
          <span>{ENTERPRISE_PERMISSION_LEVELS[permissionLevel]}</span>
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent className='z-[100000]'>
        {Object.keys(isEnterpriseSuperOrg ? ENTERPRISE_PERMISSION_LEVELS : PERMISSION_LEVELS).map((key) => (
          <DropdownMenuCheckboxItem
            key={key}
            checked={ENTERPRISE_PERMISSION_LEVELS[key] === ENTERPRISE_PERMISSION_LEVELS[permissionLevel]}
            onCheckedChange={() => {
              setPermissionLevel(key as PermissionLevel);
            }}
          >
            {ENTERPRISE_PERMISSION_LEVELS[key]}
          </DropdownMenuCheckboxItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

const ApproveRequestDropdown = ({ teamId, email, permissionLevel }: ApproveRequestDropdownProps) => {
  const [requestState, setRequestState] = useState(null);
  const { data: account } = useAccountInfo();
  const { acceptRequest, rejectRequest } = useMultiOrgAccessRequestList(account?.organizations);

  return requestState ? (
    <span className='w-[130px] text-left text-xs text-slate-500'>{requestState}</span>
  ) : (
    <div className='flex flex-row justify-between'>
      <Button
        size='small'
        onClick={() => {
          acceptRequest(
            {
              activeTeamId: teamId,
              email,
              permissionLevel
            },
            {
              onSuccess: () => {
                setRequestState('Approved');
                trackEvent('responded_to_access_request');
                trackEvent('approved_access_request', {
                  approved_user: email,
                  team_id: teamId
                });
              },
              onError: (e: { detail: string; status: number }) => {
                toast({
                  title: 'Access Request Failed!',
                  icon: faWarning,
                  variant: 'error',
                  description: e?.detail ?? 'There was something wrong'
                });
              }
            }
          );
        }}
      >
        Approve
      </Button>
      <Button
        className='ml-2'
        size='small'
        variant='secondary'
        onClick={() => {
          rejectRequest(
            {
              activeTeamId: teamId,
              email
            },
            {
              onSuccess: () => {
                setRequestState('Rejected');
                trackEvent('responded_to_access_request');
                trackEvent('rejected_access_request', {
                  rejected_user: email,
                  team_id: teamId
                });
              },
              onError: (e: { detail: string; status: number }) => {
                toast({
                  title: 'Access Request Failed!',
                  icon: faWarning,
                  variant: 'error',
                  description: e?.detail ?? 'There was something wrong'
                });
              }
            }
          );
        }}
      >
        Reject
      </Button>
    </div>
  );
};

const UserRequest = ({ teamId, teamName, email, name, isEnterpriseSuperOrg, profilePictureUrl }: UserRequestProps) => {
  const [permissionLevel, setPermissionLevel] = useState<PermissionLevel>('write');

  return (
    <TableRow>
      <TableCell>
        <RequestProfileCard profilePictureUrl={profilePictureUrl} name={name} email={email} />
      </TableCell>
      <TableCell>{teamName}</TableCell>
      <TableCell>
        <PermissionLevelDropdown
          isEnterpriseSuperOrg={isEnterpriseSuperOrg}
          permissionLevel={permissionLevel}
          setPermissionLevel={setPermissionLevel}
        />
      </TableCell>
      <TableCell>
        <ApproveRequestDropdown teamId={teamId} email={email} permissionLevel={permissionLevel} />
      </TableCell>
    </TableRow>
  );
};

export default UserRequest;
